import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seo from '../seo/seoRoutes.json';
import faq from '../seo/faq.json';
import { faqSchema, webPageSchema } from '../seo/schema.mjs';

const { site, routes } = seo;
const OWNED = '[data-rh], [data-seo]';
const NONE = [];

// Per-route <head> management: title, description, canonical, OG/Twitter and
// JSON-LD. Replaces the prerendered/static tags (marked data-rh) in place so
// the document never carries two canonicals. Organization/WebSite schema are
// emitted statically by the prerender step and re-added here for SPA navs.
export default function Seo({ path, title, description, jsonLd = NONE }) {
  const { pathname } = useLocation();
  const key = path || pathname.replace(/\/+$/, '') || '/';
  const route = routes[key] || routes['/'];
  const t = title || route.title;
  const d = description || route.description;
  const canonical = `${site.url}${key === '/' ? '/' : key}`;

  useEffect(() => {
    const head = document.head;
    // Keep the static Organization/WebSite schema; drop everything else we own.
    head.querySelectorAll(OWNED).forEach((el) => {
      const keep = el.tagName === 'SCRIPT' && /^\{"@context":"https:\/\/schema\.org","@type":(\["EducationalOrganization"|"WebSite")/.test(el.textContent || '');
      if (!keep) el.remove();
    });
    document.title = t;

    const add = (tag, attrs, text) => {
      const el = document.createElement(tag);
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      if (text) el.textContent = text;
      el.setAttribute('data-seo', 'true');
      head.appendChild(el);
    };
    add('meta', { name: 'description', content: d });
    add('link', { rel: 'canonical', href: canonical });
    [['og:type', 'website'], ['og:site_name', site.name], ['og:url', canonical], ['og:title', t],
      ['og:description', d], ['og:image', site.ogImage], ['og:image:width', '1200'],
      ['og:image:height', '630'], ['og:locale', 'en_US']]
      .forEach(([p, c]) => add('meta', { property: p, content: c }));
    [['twitter:card', 'summary_large_image'], ['twitter:title', t], ['twitter:description', d], ['twitter:image', site.ogImage]]
      .forEach(([n, c]) => add('meta', { name: n, content: c }));
    const schemas = [webPageSchema(site, key, route), ...(route.faq ? [faqSchema(faq)] : []), ...jsonLd];
    schemas.forEach((s) => add('script', { type: 'application/ld+json' }, JSON.stringify(s)));
  }, [key, t, d, canonical, route, jsonLd]);

  return null;
}
